import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Resend cooldown timer. Returns the seconds remaining (0 = ready) and a `start` function to
 * (re)arm the cooldown for `seconds`. Ticks down once per second and cleans up on unmount.
 */
export function useResendCooldown(seconds: number): { remaining: number; start: () => void } {
	const [remaining, setRemaining] = useState(0);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const clear = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	}, []);

	const start = useCallback(() => {
		clear();
		setRemaining(seconds);
		intervalRef.current = setInterval(() => {
			setRemaining((prev) => {
				if (prev <= 1) {
					clear();
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	}, [seconds, clear]);

	useEffect(() => clear, [clear]);

	return { remaining, start };
}
